import { Link } from "react-router-dom";
import { useThreads, formatThreadTime } from "@/lib/chat";
import { MessageCircle } from "lucide-react";

export default function ChatThreadList({ activeThreadId }) {
  const { data: threads, isLoading } = useThreads({ poll: true });

  if (isLoading) {
    return (
      <div data-testid="chat-list-loading" className="p-4 space-y-2">
        {[0, 1].map((i) => (
          <div key={i} className="h-16 bg-white/5 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!threads || threads.length === 0) {
    return (
      <div
        data-testid="chat-empty-list"
        className="flex-1 flex flex-col items-center justify-center p-6 text-center"
      >
        <MessageCircle className="h-8 w-8 text-[#A1A1AA] mb-3" />
        <p className="font-body text-sm text-white">No conversations yet.</p>
        <p className="font-body text-xs text-[#A1A1AA] mt-2 max-w-xs">
          Open chat on a radar match to start trading.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="chat-thread-list" className="flex-1 overflow-y-auto">
      <header className="sticky top-0 bg-black/95 backdrop-blur-md border-b border-white/5 px-4 py-3 z-10">
        <h2 className="font-display text-base font-bold text-white">Messages</h2>
      </header>
      <ul>
        {threads.map((t) => {
          const initial = (t.other_participant.display_name || "?")
            .charAt(0)
            .toUpperCase();
          const active = t.thread_id === activeThreadId;
          return (
            <li key={t.thread_id}>
              <Link
                to={`/app/chat/${t.thread_id}`}
                data-testid={`thread-row-${t.thread_id}`}
                className={
                  "flex items-center gap-3 px-4 py-3 border-b border-white/5 " +
                  "hover:bg-white/5 transition-colors " +
                  (active ? "bg-[#E5B83A]/5 border-l-2 border-l-[#E5B83A]" : "")
                }
              >
                <span className="h-10 w-10 rounded-full bg-gradient-to-br from-[#E5B83A] to-[#C9971C] text-black font-bold flex items-center justify-center shrink-0">
                  {initial}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="font-body text-sm font-medium text-white truncate"
                      data-testid={`thread-name-${t.thread_id}`}
                    >
                      {t.other_participant.display_name}
                    </span>
                    <span className="text-[10px] text-[#A1A1AA] font-body shrink-0">
                      {formatThreadTime(t.last_message_at)}
                    </span>
                  </div>
                  <p
                    data-testid={`thread-preview-${t.thread_id}`}
                    className={
                      "text-xs truncate mt-0.5 font-body " +
                      (t.last_message_preview ? "text-[#A1A1AA]" : "italic text-[#666]")
                    }
                  >
                    {t.last_message_preview || "No messages yet"}
                  </p>
                </div>
                {t.unread_count > 0 ? (
                  <span
                    data-testid={`thread-unread-${t.thread_id}`}
                    className="h-5 min-w-[20px] px-1.5 rounded-full bg-[#E5B83A] text-black text-[10px] font-bold flex items-center justify-center"
                  >
                    {t.unread_count}
                  </span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
